'use client';

import React, { FormEvent, useId, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  Mail,
  MapPin,
  Phone,
  Send,
  User,
  Users,
} from 'lucide-react';

const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.in',
  'yahoo.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'gmx.com',
  'mail.com',
]);

const COUNTRY_CODES = [
  { code: '+91', country: 'India', iso: 'IN', digits: 10 },
  { code: '+1', country: 'United States / Canada', iso: 'US', digits: 10 },
  { code: '+44', country: 'United Kingdom', iso: 'GB', digits: 10 },
  { code: '+61', country: 'Australia', iso: 'AU', digits: 9 },
  { code: '+65', country: 'Singapore', iso: 'SG', digits: 8 },
  { code: '+971', country: 'United Arab Emirates', iso: 'AE', digits: 9 },
  { code: '+966', country: 'Saudi Arabia', iso: 'SA', digits: 9 },
  { code: '+974', country: 'Qatar', iso: 'QA', digits: 8 },
  { code: '+60', country: 'Malaysia', iso: 'MY', digits: 9 },
  { code: '+49', country: 'Germany', iso: 'DE', digits: 10 },
  { code: '+33', country: 'France', iso: 'FR', digits: 9 },
  { code: '+81', country: 'Japan', iso: 'JP', digits: 10 },
] as const;

const industries = [
  'Manufacturing',
  'Garments & Textiles',
  'IT & Technology',
  'Construction',
  'Healthcare',
  'Retail & Distribution',
  'Professional Services',
  'Other',
];

const employeeRanges = ['0-25', '26-50', '51+'];

const modules = [
  'Employee Management',
  'Attendance & Workforce',
  'Leave Management',
  'Payroll',
  'Performance',
  'Project Planning',
  'Production Management',
  'Breakdown Management',
  'Inventory Management',
  'Recruitment & Onboarding',
  'Analytics & Reports',
  'AI Assistant',
];

function MentroxLogo({ dark = false, compact = false }: { dark?: boolean; compact?: boolean }) {
  return (
    <Link
      href="/"
      aria-label="Mentrox OneHR"
      className={['inline-flex items-center gap-2.5', compact ? 'scale-95 origin-left' : ''].join(
        ' '
      )}
    >
      <span
        className={[
          'relative flex items-center justify-center font-black leading-none',
          compact ? 'h-8 w-8 text-base' : 'h-10 w-10 text-lg',
          dark ? 'text-white' : 'text-[#0A1C33]',
        ].join(' ')}
      >
        <span className="absolute inset-0 rounded-[9px] bg-orange-500" />
        <span className={['relative z-10', dark ? 'text-white' : 'text-white'].join(' ')}>M</span>
      </span>

      <span
        className={[
          'font-bold tracking-[-0.03em]',
          compact ? 'text-sm' : 'text-lg',
          dark ? 'text-white' : 'text-[#0A1C33]',
        ].join(' ')}
      >
        Mentrox<span className="text-orange-500">OneHR</span>
      </span>
    </Link>
  );
}

export default function RequestDemoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedRequestId, setSubmittedRequestId] = useState<string | null>(null);

  const [countryCode, setCountryCode] = useState('+91');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [form, setForm] = useState({
    companyName: '',
    industry: '',
    employeeCount: '',
    fullName: '',
    workEmail: '',
    phone: '',
    website: '',
    city: '',
    state: '',
    gstin: '',
    selectedModules: [] as string[],
    message: '',
  });

  const setField = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleModule = (module: string) => {
    setForm((current) => ({
      ...current,
      selectedModules: current.selectedModules.includes(module)
        ? current.selectedModules.filter((item) => item !== module)
        : [...current.selectedModules, module],
    }));
  };

  const selectedCountry =
    COUNTRY_CODES.find((item) => item.code === countryCode) ?? COUNTRY_CODES[0];

  const validateBusinessEmail = (email: string) => {
    const normalized = email.trim().toLowerCase();
    const match = normalized.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);

    if (!match) {
      return 'Enter a valid company email address.';
    }

    const domain = match[1];

    if (FREE_EMAIL_PROVIDERS.has(domain)) {
      return 'Please use your company email, not a personal/free email address.';
    }

    return '';
  };

  const handleEmailChange = (value: string) => {
    setField('workEmail', value);
    setEmailError(value ? validateBusinessEmail(value) : '');
  };

  const handlePhoneChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, selectedCountry.digits);
    setField('phone', digitsOnly);

    if (!digitsOnly) {
      setPhoneError('');
    } else if (digitsOnly.length !== selectedCountry.digits) {
      setPhoneError(
        `Enter exactly ${selectedCountry.digits} digits for ${selectedCountry.country}.`
      );
    } else {
      setPhoneError('');
    }
  };

  const handleCountryChange = (code: string) => {
    setCountryCode(code);
    const country = COUNTRY_CODES.find((item) => item.code === code) ?? COUNTRY_CODES[0];

    const digitsOnly = form.phone.replace(/\D/g, '').slice(0, country.digits);

    setForm((current) => ({
      ...current,
      phone: digitsOnly,
    }));

    setPhoneError(
      digitsOnly && digitsOnly.length !== country.digits
        ? `Enter exactly ${country.digits} digits for ${country.country}.`
        : ''
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextEmailError = validateBusinessEmail(form.workEmail);
    const nextPhoneError =
      form.phone.length !== selectedCountry.digits
        ? `Enter exactly ${selectedCountry.digits} digits for ${selectedCountry.country}.`
        : '';

    setEmailError(nextEmailError);
    setPhoneError(nextPhoneError);
    setSubmitError('');

    if (
      !form.companyName.trim() ||
      !form.industry ||
      !form.employeeCount ||
      !form.fullName.trim() ||
      !form.workEmail.trim() ||
      !form.phone ||
      nextEmailError ||
      nextPhoneError
    ) {
      return;
    }

    if (submitting) return;

    setSubmitting(true);

    try {
      // Exact payload contract used by the old working RequestHRMSForm.
      const payload = {
        companyName: form.companyName.trim(),
        industry: form.industry,
        employeeCount: form.employeeCount,
        adminName: form.fullName.trim(),
        adminEmail: form.workEmail.trim().toLowerCase(),
        phone: `${selectedCountry.code}${form.phone}`,
        website: form.website.trim(),
        city: form.city.trim(),
        state: form.state,
        gstin: form.gstin.trim().toUpperCase(),
        requestedModules: form.selectedModules,
        message: form.message.trim(),
      };

      const response = await fetch('/api/companies/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        if (result?.code === 'REQUEST_ALREADY_EXISTS') {
          setSubmitError(result.message || 'A request from this email is already under review.');
        } else {
          setSubmitError(result?.message || 'Unable to submit your request.');
        }
        return;
      }

      setSubmittedRequestId(result.request?.id || null);
      setSubmitted(true);

      setForm({
        companyName: '',
        industry: '',
        employeeCount: '',
        fullName: '',
        workEmail: '',
        phone: '',
        website: '',
        city: '',
        state: '',
        gstin: '',
        selectedModules: [],
        message: '',
      });
      setCountryCode('+91');
      setEmailError('');
      setPhoneError('');
      setSubmitError('');
    } catch (error) {
      console.error('[REQUEST_DEMO_PAGE]', error);
      setSubmitError('Unable to connect to Mentrox right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="demo-form" className="scroll-mt-24 bg-[#F5F7FA] text-[#0A1C33]">
      <div className="mx-auto max-w-[1240px]">
        {/* RIGHT: demo form */}
        <section className="min-w-0 bg-[#F5F7FA]">
          <div className="mx-auto w-full max-w-[760px] px-4 py-5 sm:px-7 sm:py-8 lg:px-10 lg:py-10">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 transition-colors hover:text-[#0A1C33]"
            >
              <ArrowLeft size={14} />
              Back to Mentrox OneHR
            </Link>

            <div className="mt-5 lg:hidden">
              <MentroxLogo />
            </div>

            <div className="mt-5 overflow-hidden border border-slate-200 bg-white shadow-[0_20px_60px_rgba(9,30,66,.08)]">
              <div className="border-b border-slate-200 px-5 py-6 sm:px-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-orange-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-600">
                  <Send size={12} />
                  Request a Demo
                </div>

                <h2 className="mt-4 text-3xl font-bold tracking-[-0.045em] text-[#0A1C33]">
                  Let&apos;s build the right OneHR setup.
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Share a few details and our team will get back to you.
                </p>
              </div>

              {submitted ? (
                <div className="px-5 py-16 text-center sm:px-7">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <CheckCircle2 size={28} />
                  </div>

                  <h3 className="mt-5 text-2xl font-bold text-[#0A1C33]">Request received.</h3>

                  <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-500">
                    Thanks for reaching out to Mentrox OneHR. Our team will contact you using the
                    details you provided.
                  </p>
                  {submittedRequestId ? (
                    <div className="mx-auto mt-5 inline-flex items-center gap-2 border border-slate-200 bg-slate-50 px-4 py-2.5">
                      <span className="text-xs text-slate-500">Request ID</span>
                      <span className="font-mono text-xs font-semibold text-[#06162C]">
                        {submittedRequestId}
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link
                      href="/"
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#c94e00] px-5 text-sm font-semibold text-white"
                    >
                      Back to Home
                      <ArrowRight size={15} />
                    </Link>

                    <Link
                      href="/contact"
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-slate-200 px-5 text-sm font-semibold text-slate-700"
                    >
                      Contact Us
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-5 py-6 sm:px-7 sm:py-7">
                  <FormSection
                    icon={<Building2 size={17} />}
                    title="Company details"
                    description="Tell us about your organization."
                  />

                  <div className="mt-6 space-y-5">
                    <Field
                      label="Company Name"
                      required
                      icon={<Building2 size={16} />}
                      placeholder="Your company name"
                      value={form.companyName}
                      onChange={(value) => setField('companyName', value)}
                    />

                    <div className="grid gap-5 sm:grid-cols-2">
                      <SelectField
                        label="Industry"
                        required
                        icon={<BriefcaseIcon />}
                        value={form.industry}
                        placeholder="Select industry"
                        options={industries}
                        onChange={(value) => setField('industry', value)}
                      />

                      <SelectField
                        label="Employees"
                        required
                        icon={<Users size={16} />}
                        value={form.employeeCount}
                        placeholder="Select employee count"
                        options={employeeRanges}
                        onChange={(value) => setField('employeeCount', value)}
                      />
                    </div>
                  </div>

                  <div className="my-8 h-px bg-slate-100" />

                  <FormSection
                    icon={<User size={17} />}
                    title="Contact information"
                    description="Who should we contact?"
                  />

                  <div className="mt-6 space-y-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Your Full Name"
                        required
                        icon={<User size={16} />}
                        placeholder="Full name"
                        value={form.fullName}
                        onChange={(value) => setField('fullName', value)}
                      />

                      <Field
                        label="Work Email"
                        required
                        type="email"
                        autoComplete="email"
                        icon={<Mail size={16} />}
                        placeholder="name@yourcompany.com"
                        value={form.workEmail}
                        onChange={handleEmailChange}
                        error={emailError}
                      />
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <PhoneField
                        countryCode={countryCode}
                        onCountryChange={handleCountryChange}
                        value={form.phone}
                        onChange={handlePhoneChange}
                        error={phoneError}
                        digits={selectedCountry.digits}
                      />

                      <Field
                        label="City"
                        icon={<MapPin size={16} />}
                        placeholder="City"
                        value={form.city}
                        onChange={(value) => setField('city', value)}
                      />
                    </div>
                  </div>

                  <div className="my-8 h-px bg-slate-100" />

                  <div className="my-8 h-px bg-slate-100" />

                  <FormSection
                    icon={<Building2 size={17} />}
                    title="Business details"
                    description="Additional company information."
                  />

                  <div className="mt-5 grid gap-5 sm:grid-cols-2">
                    <Field
                      label="Website"
                      type="url"
                      placeholder="https://yourcompany.com"
                      value={form.website}
                      onChange={(value) => setField('website', value)}
                    />

                    <Field
                      label="GSTIN"
                      placeholder="22AAAAA0000A1Z5"
                      value={form.gstin}
                      onChange={(value) => setField('gstin', value.toUpperCase().slice(0, 15))}
                    />

                    <SelectField
                      label="State"
                      value={form.state}
                      placeholder="Select state"
                      options={[
                        'Andhra Pradesh',
                        'Karnataka',
                        'Kerala',
                        'Maharashtra',
                        'Tamil Nadu',
                        'Telangana',
                        'Delhi',
                        'Gujarat',
                        'Rajasthan',
                        'West Bengal',
                        'Uttar Pradesh',
                        'Madhya Pradesh',
                        'Punjab',
                        'Haryana',
                        'Odisha',
                      ]}
                      onChange={(value) => setField('state', value)}
                    />
                  </div>

                  <FormSection
                    icon={<LayersIcon />}
                    title="What would you like to explore?"
                    description="Select the modules relevant to your business."
                  />

                  <div className="mt-5 grid gap-2 sm:grid-cols-2">
                    {modules.map((module) => {
                      const selected = form.selectedModules.includes(module);

                      return (
                        <button
                          key={module}
                          type="button"
                          onClick={() => toggleModule(module)}
                          className={[
                            'flex min-h-12 items-center justify-between border px-3.5 text-left text-xs font-medium transition-colors',
                            selected
                              ? 'border-orange-300 bg-orange-50 text-[#0A1C33]'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-orange-200 hover:bg-orange-50/40',
                          ].join(' ')}
                        >
                          <span>{module}</span>
                          <span
                            className={[
                              'flex h-5 w-5 items-center justify-center rounded-full border',
                              selected
                                ? 'border-orange-500 bg-orange-500 text-white'
                                : 'border-slate-200 text-transparent',
                            ].join(' ')}
                          >
                            <CheckCircle2 size={12} />
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-6">
                    <label
                      htmlFor="demo-requirements"
                      className="text-xs font-semibold text-[#0A1C33]"
                    >
                      Tell us about your requirements
                    </label>
                    <textarea
                      id="demo-requirements"
                      rows={4}
                      value={form.message}
                      onChange={(event) => setField('message', event.target.value)}
                      placeholder="What are you looking to improve or manage?"
                      className="mt-2 w-full resize-none border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#0A1C33] outline-none transition-colors placeholder:text-slate-500 focus:border-orange-300 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  {submitError ? (
                    <div
                      role="alert"
                      className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-xs leading-5 text-red-600"
                    >
                      {submitError}
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-7 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#c94e00] px-6 text-sm font-semibold text-white shadow-[0_15px_35px_rgba(255,106,0,.18)] transition-all hover:-translate-y-0.5 hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : 'Request a Demo'}
                    {!submitting ? <ArrowRight size={16} /> : null}
                  </button>

                  <p className="mt-4 text-center text-[10px] leading-5 text-slate-600">
                    By submitting this form, you agree to be contacted by Mentrox regarding your
                    demo request.
                  </p>
                </form>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] text-slate-600">
              <a href="mailto:mentroxdigitalsolution@gmail.com" className="hover:text-orange-600">
                mentroxdigitalsolution@gmail.com
              </a>
              <a href="mailto:support@mentrox.in" className="hover:text-orange-600">
                support@mentrox.in
              </a>
              <a href="tel:+919499914968" className="hover:text-orange-600">
                +91 9499914968
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function FormSection({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-600">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-bold text-[#0A1C33]">{title}</h3>
        <p className="mt-0.5 text-[10px] text-slate-600">{description}</p>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  icon,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  autoComplete,
}: {
  label: string;
  required?: boolean;
  icon?: React.ReactNode;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  autoComplete?: string;
}) {
  const inputId = useId();
  return (
    <div>
      <label htmlFor={inputId} className="text-xs font-semibold text-[#0A1C33]">
        {label}
        {required ? <span className="ml-1 text-orange-500">*</span> : null}
      </label>

      <div className="relative mt-2">
        <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600">
          {icon}
        </div>

        <input
          id={inputId}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          required={required}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={[
            'h-12 w-full border bg-white pl-10 pr-3.5 text-sm text-[#0A1C33] outline-none transition-colors placeholder:text-slate-500 focus:ring-2',
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-200 focus:border-orange-300 focus:ring-orange-100',
          ].join(' ')}
        />
      </div>

      {error ? <p className="mt-1.5 text-[10px] leading-4 text-red-500">{error}</p> : null}
    </div>
  );
}

function PhoneField({
  countryCode,
  onCountryChange,
  value,
  onChange,
  error,
  digits,
}: {
  countryCode: string;
  onCountryChange: (value: string) => void;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  digits: number;
}) {
  const inputId = useId();
  return (
    <div>
      <label htmlFor={inputId} className="text-xs font-semibold text-[#0A1C33]">
        Phone<span className="ml-1 text-orange-500">*</span>
      </label>

      <div
        className={[
          'mt-2 flex h-12 w-full border bg-white transition-colors focus-within:ring-2',
          error
            ? 'border-red-300 focus-within:border-red-400 focus-within:ring-red-100'
            : 'border-slate-200 focus-within:border-orange-300 focus-within:ring-orange-100',
        ].join(' ')}
      >
        <div className="relative flex shrink-0 items-center border-r border-slate-200">
          <select
            aria-label="Country code"
            value={countryCode}
            onChange={(event) => onCountryChange(event.target.value)}
            className="h-full appearance-none bg-transparent pl-3 pr-7 text-sm font-semibold text-[#0A1C33] outline-none"
          >
            {COUNTRY_CODES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.code} {country.iso}
              </option>
            ))}
          </select>

          <ChevronDown
            size={13}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-600"
          />
        </div>

        <div className="relative flex min-w-0 flex-1 items-center">
          <Phone size={16} className="pointer-events-none absolute left-3.5 text-slate-600" />

          <input
            id={inputId}
            aria-invalid={Boolean(error)}
            required
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            maxLength={digits}
            placeholder={`Enter ${digits}-digit number`}
            className="h-full min-w-0 flex-1 bg-transparent pl-10 pr-3.5 text-sm text-[#0A1C33] outline-none placeholder:text-slate-500"
          />
        </div>
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-3">
        <span className="text-[9px] text-slate-600">
          {value.length}/{digits} digits
        </span>
        {error ? (
          <span className="text-right text-[10px] leading-4 text-red-500">{error}</span>
        ) : null}
      </div>
    </div>
  );
}

function SelectField({
  label,
  required,
  icon,
  value,
  placeholder,
  options,
  onChange,
}: {
  label: string;
  required?: boolean;
  icon?: React.ReactNode;
  value: string;
  placeholder: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  return (
    <div>
      <label htmlFor={inputId} className="text-xs font-semibold text-[#0A1C33]">
        {label}
        {required ? <span className="ml-1 text-orange-500">*</span> : null}
      </label>

      <div className="relative mt-2">
        <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600">
          {icon}
        </div>

        <select
          id={inputId}
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-12 w-full appearance-none border border-slate-200 bg-white pl-10 pr-10 text-sm text-[#0A1C33] outline-none transition-colors focus:border-orange-300 focus:ring-2 focus:ring-orange-100"
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <ChevronDown
          size={15}
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600"
        />
      </div>
    </div>
  );
}

function BriefcaseIcon() {
  return <span className="text-[13px]">▣</span>;
}

function LayersIcon() {
  return <span className="text-[13px]">✦</span>;
}
